import { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';

/**
 * Floating WhatsApp CTA — appears after 3s to avoid competing with LCP.
 * Contextual pre-filled message per route.
 * Hidden on /admin and /badge routes.
 */
const PHONE = '34602645005';

const PREFILL = {
  fr: {
    default:           'Bonjour, je souhaite discuter de mon projet sur la Costa Brava.',
    '/services/construction': 'Bonjour, je m\'intéresse à une construction neuve sur la Costa Brava.',
    '/services/renovation':   'Bonjour, je souhaite rénover une villa sur la Costa Brava.',
    '/services/piscine':      'Bonjour, je souhaite en savoir plus sur la construction de piscines.',
    '/services/exterieur':    'Bonjour, je m\'intéresse aux aménagements extérieurs.',
    '/services/maconnerie':   'Bonjour, j\'ai un projet de maçonnerie.',
    '/services/interieur':    'Bonjour, je souhaite des informations sur l\'aménagement intérieur.',
    '/contact':               'Bonjour, je vous contacte suite à la page Contact.',
    '/realisations':          'Bonjour, j\'ai vu vos réalisations et j\'aimerais discuter de mon projet.',
  },
  en: {
    default: 'Hello, I\'d like to discuss my project on the Costa Brava.',
    '/services/construction': 'Hello, I\'m interested in new construction on the Costa Brava.',
    '/services/renovation':   'Hello, I\'d like to renovate a villa on the Costa Brava.',
    '/services/piscine':      'Hello, I\'d like to learn more about pool construction.',
    '/services/exterieur':    'Hello, I\'m interested in outdoor developments.',
    '/services/maconnerie':   'Hello, I have a masonry project.',
    '/services/interieur':    'Hello, I\'d like information on interior design.',
  },
  de: {
    default: 'Guten Tag, ich möchte mein Projekt an der Costa Brava besprechen.',
  },
  es: {
    default: 'Hola, me gustaría hablar sobre mi proyecto en la Costa Brava.',
    '/services/construction': 'Hola, me interesa una construcción nueva en la Costa Brava.',
    '/services/renovation':   'Hola, quiero reformar una villa en la Costa Brava.',
    '/services/piscine':      'Hola, me gustaría información sobre construcción de piscinas.',
  },
  ru: {
    default: 'Здравствуйте, я хотел бы обсудить мой проект на Коста-Браве.',
  },
};

const WhatsAppButton = () => {
  const [visible, setVisible] = useState(false);
  const location = useLocation();
  const { language } = useLanguage();

  // Don't render on admin/badge/sign routes
  const hidden =
    location.pathname.startsWith('/admin') ||
    location.pathname.startsWith('/badge') ||
    location.pathname.startsWith('/sign') ||
    location.pathname === '/login';

  useEffect(() => {
    if (hidden) return;
    const t = setTimeout(() => setVisible(true), 2500);
    return () => clearTimeout(t);
  }, [hidden]);

  if (hidden) return null;

  const lang = PREFILL[language] || PREFILL.fr;
  const message = lang[location.pathname] || lang.default;
  const href = `https://wa.me/${PHONE}?text=${encodeURIComponent(message)}`;

  return (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      aria-label="WhatsApp"
      className={`
        fixed bottom-6 right-6 z-50
        w-14 h-14 rounded-full
        bg-[#25D366] hover:bg-[#1da851]
        flex items-center justify-center
        shadow-lg hover:shadow-xl
        transition-all duration-300
        ${visible ? 'opacity-100 scale-100 translate-y-0' : 'opacity-0 scale-50 translate-y-4 pointer-events-none'}
      `}
      style={{ transitionTimingFunction: 'cubic-bezier(0.16, 1, 0.3, 1)' }}
    >
      <svg
        viewBox="0 0 24 24"
        className="w-7 h-7 fill-white"
        aria-hidden="true"
      >
        <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z" />
      </svg>
    </a>
  );
};

export default WhatsAppButton;
